import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CallButton from "@/components/CallButton";
import SEO from "@/components/SEO";
import ServiceStructuredData from "@/components/ServiceStructuredData";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEOSubHero from "@/components/SEOSubHero";
import { Link } from "react-router-dom";
import { Phone, Shield, CheckCircle2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import heroImage from "@/assets/heroes/porte-blindee-hero.jpg";
import gallery1 from "@/assets/gallery/porte-blindee-1.jpg";
import gallery2 from "@/assets/gallery/porte-blindee-2.jpg";
import gallery3 from "@/assets/gallery/porte-blindee-3.jpg";

const PorteBlindeeGarage = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="Porte Blindée & Garage Villeurbanne | Installation Sécurisée"
        description="Installation de portes blindées et portes de garage à Villeurbanne. Sécurisation optimale de votre domicile. Devis gratuit, intervention rapide par professionnel."
        keywords="porte blindée Villeurbanne, installation porte blindée Lyon, porte de garage 69, sécurisation domicile"
        canonical="https://www.tolstoi-serrurier.fr/porte-blindee-garage"
        ogUrl="https://www.tolstoi-serrurier.fr/porte-blindee-garage"
        ogImage="https://www.tolstoi-serrurier.fr/logo.jpg"
        ogType="business.business"
      />
      <ServiceStructuredData 
        serviceName="Installation porte blindée et garage"
        serviceDescription="Installation de portes blindées certifiées A2P et portes de garage à Villeurbanne. Sécurisation complète de votre domicile."
        serviceUrl="https://www.tolstoi-serrurier.fr/porte-blindee-garage"
      />
      <Header />
      
      <div className="container mx-auto px-4">
        <Breadcrumbs items={[
          { label: "Accueil", href: "/" },
          { label: "Porte blindée & garage", href: "/porte-blindee-garage" }
        ]} />
      </div>
      
      <main className="pt-24">
        {/* Hero Section with Background Image */}
        <section className="relative bg-secondary text-secondary-foreground py-16 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img 
              src={heroImage} 
              alt="Serrurier Villeurbanne - Installation porte blindée haute sécurité"
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-secondary/90 to-secondary/70" />
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Porte Blindée & Garage à Villeurbanne
              </h1>
              <p className="text-xl mb-8 text-secondary-foreground/90">
                Installation de portes blindées et portes de garage pour sécuriser votre domicile
              </p>
              <Button size="lg" variant="default" className="gap-2" asChild>
                <a href="tel:0482531608">
                  <Phone className="h-5 w-5" />
                  Appeler maintenant : 04 82 53 16 08
                </a>
              </Button>
            </div>
          </div>
        </section>

        <SEOSubHero title="Porte blindée et porte de garage — sécurisation complète à Villeurbanne">
          <p>
            Installation de <strong>portes blindées certifiées A2P (BP1 à BP3)</strong> avec serrures multipoints et isolations renforcées. Conseils personnalisés selon votre budget et les exigences assurance.
          </p>
          <p>
            Pose et motorisation de <strong>portes de garage</strong> (sectionnelles, enroulement, basculantes) avec systèmes anti-effraction. Pour compléter, consultez le <Link to="/changement-serrure" className="text-primary hover:underline">changement de serrure</Link> ou le <Link to="/depannage-urgence" className="text-primary hover:underline">dépannage urgence</Link>.
          </p>
          <p>
            Intervention à Villeurbanne et partout à Lyon. <Link to="/zones-intervention" className="text-primary hover:underline">Découvrir nos zones</Link>.
          </p>
        </SEOSubHero>

        {/* Main Content */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="prose prose-lg max-w-none mb-12">
                <h2 className="text-3xl font-bold mb-6">Sécurisation de votre domicile</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Tolstoï Serrurier vous accompagne dans la sécurisation de votre habitation à Villeurbanne et dans 
                  toute la métropole lyonnaise. Que ce soit pour l'installation d'une porte blindée ou d'une porte de 
                  garage, nous vous proposons des solutions adaptées à vos besoins de sécurité et votre budget.
                </p>
                
                <h3 className="text-2xl font-semibold mb-4 mt-8">Protection renforcée</h3>
                <p className="text-muted-foreground mb-6">
                  Les portes blindées et portes de garage sécurisées constituent la première barrière de protection 
                  contre les cambriolages. Nous installons exclusivement des produits certifiés et conformes aux 
                  exigences des compagnies d'assurance.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-12">
                <Card>
                  <CardContent className="pt-6">
                    <Shield className="h-12 w-12 text-primary mb-4" />
                    <h3 className="font-semibold text-lg mb-2">Portes blindées</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Installation de portes blindées certifiées A2P BP1, BP2 ou BP3 selon vos besoins de sécurité.
                    </p>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>Blindage complet ou bloc-porte</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>Serrure multipoints haute sécurité</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>Panneau décoratif personnalisable</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>Isolation thermique et phonique</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <Lock className="h-12 w-12 text-primary mb-4" />
                    <h3 className="font-semibold text-lg mb-2">Portes de garage</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Installation et motorisation de portes de garage sectionnelles, basculantes ou à enroulement.
                    </p>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>Motorisation avec télécommande</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>Système anti-effraction</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>Isolation thermique</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>Design et coloris au choix</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>

              <div className="bg-muted/50 rounded-xl p-8 mb-12">
                <h3 className="text-2xl font-semibold mb-4">Certifications et normes</h3>
                <p className="text-muted-foreground mb-4">
                  Toutes nos portes blindées sont certifiées A2P (Assurance Prévention Protection) et répondent 
                  aux exigences des compagnies d'assurance :
                </p>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-background p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">A2P BP1</h4>
                    <p className="text-sm text-muted-foreground">
                      Résistance 5 minutes • Protection standard
                    </p>
                  </div>
                  <div className="bg-background p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">A2P BP2</h4>
                    <p className="text-sm text-muted-foreground">
                      Résistance 10 minutes • Protection intermédiaire
                    </p>
                  </div>
                  <div className="bg-background p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">A2P BP3</h4>
                    <p className="text-sm text-muted-foreground">
                      Résistance 15 minutes • Protection maximale
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-primary/10 rounded-xl p-8 mb-12">
                <h3 className="text-2xl font-semibold mb-4">Pourquoi installer une porte blindée ?</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span><strong>Sécurité renforcée :</strong> Dissuasion et résistance face aux tentatives d'effraction</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span><strong>Économies d'assurance :</strong> Réduction de la prime d'assurance habitation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span><strong>Isolation :</strong> Amélioration thermique et phonique de votre entrée</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span><strong>Esthétique :</strong> Large choix de finitions pour s'adapter à votre décoration</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span><strong>Valeur immobilière :</strong> Augmentation de la valeur de votre bien</span>
                  </li>
                </ul>
              </div>

              <div className="bg-secondary text-secondary-foreground rounded-xl p-8 text-center">
                <h3 className="text-2xl font-bold mb-4">Besoin de sécuriser votre domicile ?</h3>
                <p className="text-lg mb-6 text-secondary-foreground/90">
                  Contactez-nous pour un devis gratuit et un conseil personnalisé
                </p>
                <Button size="lg" variant="default" className="gap-2" asChild>
                  <a href="tel:0482531608">
                    <Phone className="h-5 w-5" />
                    04 82 53 16 08
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Photo Gallery Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-4">
                Nos Installations en Images
              </h2>
              <p className="text-center text-muted-foreground mb-12">
                Découvrez la qualité de notre travail
              </p>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div className="relative group overflow-hidden rounded-xl shadow-lg">
                  <img 
                    src={gallery1}
                    alt="Porte blindée avec certification A2P serrure multipoints Villeurbanne"
                    loading="lazy"
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-4 left-4 text-white">
                      <p className="font-semibold">Certification A2P</p>
                    </div>
                  </div>
                </div>
                
                <div className="relative group overflow-hidden rounded-xl shadow-lg">
                  <img 
                    src={gallery2}
                    alt="Installation porte garage sectionnelle professionnelle Lyon"
                    loading="lazy"
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-4 left-4 text-white">
                      <p className="font-semibold">Porte Garage</p>
                    </div>
                  </div>
                </div>
                
                <div className="relative group overflow-hidden rounded-xl shadow-lg">
                  <img 
                    src={gallery3}
                    alt="Porte blindée installée entrée maison élégante haute sécurité Villeurbanne"
                    loading="lazy"
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-4 left-4 text-white">
                      <p className="font-semibold">Installation Finale</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
      <CallButton />
    </div>
  );
};

export default PorteBlindeeGarage;